import { gridClient } from '@/utils/apiUtil'

/**
 * Free trial enrolment — `PATCH /campaign/free-trial/enroll/:campaignId/:customerId`
 * from the "Grid: Campaigns" Postman collection.
 *
 * That collection is documented as `SUPER`-role only ("intended for internal
 * platform administrators"), so a customer's own token is expected to come back
 * `403` until the backend exposes a customer-scoped self-enrolment route. The
 * outcome is therefore reported rather than thrown, and callers must treat
 * anything but `enrolled`/`already-enrolled` as "this customer has no trial".
 */
export type FreeTrialEnrollmentOutcome =
  | 'enrolled'
  | 'already-enrolled'
  | 'forbidden'
  | 'ineligible'

type EnrollArgs = {
  campaignId: string
  customerId: string
}

export const enrollInFreeTrialRequest = async (
  { campaignId, customerId }: EnrollArgs,
): Promise<FreeTrialEnrollmentOutcome> => {
  const { status } = await gridClient.patch(
    `/campaign/free-trial/enroll/${campaignId}/${customerId}`,
    undefined,
    {
      // Read the status here so `409 already enrolled` can be told apart from a
      // real failure — the shared interceptor normalizes errors and drops it.
      // 401 is deliberately left to throw so that interceptor can still refresh
      // the token and retry the request.
      validateStatus: code => code < 500 && code !== 401,
    },
  )

  if (status === 200 || status === 201) {
    return 'enrolled'
  }
  if (status === 409) {
    return 'already-enrolled'
  }
  if (status === 403) {
    return 'forbidden'
  }

  return 'ineligible'
}

export const isEnrolled = (outcome: FreeTrialEnrollmentOutcome): boolean => (
  outcome === 'enrolled' || outcome === 'already-enrolled'
)

/**
 * Free trial terms — `PATCH /customer/:customerId/free-trial/terms/accept` from
 * the "Grid: Verify" Postman collection.
 *
 * Acceptance is recorded against the customer rather than the user, and the
 * backend refuses `enrollCustomerInFreeTrialCampaign` until the flag is set, so
 * this has to land before enrolment is attempted.
 */
export const acceptFreeTrialTermsRequest = async (customerId: string, campaignId?: string): Promise<void> => {
  await gridClient.patch(`/customer/${customerId}/free-trial/terms/accept`, undefined, {
    params: campaignId
      ? { campaignId }
      : undefined,
  })
}

/**
 * The agreement the customer has to read — `GET
 * /customer/:customerId/free-trial/terms/pdf`.
 *
 * The PDF is rendered per customer (their name and an effective date are
 * written into it), so it is fetched with the bearer token as a binary body
 * rather than linked to directly.
 */
export const getFreeTrialTermsPdfRequest = async (customerId: string, campaignId?: string): Promise<Blob> => {
  const { data } = await gridClient.get<Blob>(
    `/customer/${customerId}/free-trial/terms/pdf`,
    {
      responseType: 'blob',
      // The shared headers ask for JSON, which some gateways honour by refusing
      // the binary body outright.
      headers: { Accept: '*/*' },
      params: campaignId
        ? { campaignId }
        : undefined,
    },
  )

  // An embed only renders inline when the blob claims to be a PDF, and the
  // content type does not always survive the trip through the proxy.
  return data.type === 'application/pdf'
    ? data
    : new Blob([data], { type: 'application/pdf' })
}
